// ============================================================
// TMR Portal Admin — Partes: as pessoas físicas que assinam e respondem pelas Empresas
//
// Wired to the Identity directory (10 B5b/B8.4). It was the portal's last mockup, and the gap had a cost:
// the cadastre of Empresa has been filing people since July with nowhere to check or correct them
// (09 §10, F10).
//
// The associations column is not decoration. A Party with no link is invisible to any scoped operator
// (`PartyRepository.ListAsync`), and today nothing fills that link except the V1 backfill and this
// screen — the Empresa cadastre resolves a person without attaching them (STATUS, sequela da B8.4).
// ============================================================
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Btn, StatusBadge } from '../../components/primitives';
import { PageHeader, SearchBox, FilterTabs, Table, Td } from '../../components/ui';
import { Skeleton, EmptyState, ErrorState } from '../../components/states';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyableText } from '../../components/CopyableText';
import { Pagination } from '../../components/Pagination';
import { PartyFormModal } from '../../components/PartyFormModal';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCPF } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useDebounced } from '../../useDebounced';
import { PARTY_PAGE_SIZE, partyExportPage, usePartyList, useSetPartyActive } from '../../data/parties/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { PARTY_EXPORT_COLUMNS } from './exportColumns';
import type { PartyListItem } from '../../data/parties/contract';
import { PartyAssociationsModal } from './PartyAssociationsModal';

type StatusFilter = 'todos' | 'ativos' | 'inativos';

export function PartiesPage() {
  const canRead = usePermission(PERMISSIONS.partiesRead);
  const canManage = usePermission(PERMISSIONS.partiesManage);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<StatusFilter>('todos');
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<PartyListItem | null>(null);
  const [toArchive, setToArchive] = useState<PartyListItem | null>(null);
  const [associations, setAssociations] = useState<PartyListItem | null>(null);

  const debouncedSearch = useDebounced(search);

  // Described once: the table pages through these filters and the export walks all of them.
  const filters = {
    search: debouncedSearch || undefined,
    active: tab === 'todos' ? undefined : tab === 'ativos',
  };

  const list = usePartyList({ ...filters, page });
  const setActive = useSetPartyActive();

  // The server gates this directory; without the permission every request would 403 and there is nothing
  // to show (skill `frontend-architecture`, Regra 5).
  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <PageHeader eyebrow="Cadastro" title="Partes" desc={DESCRIPTION} />
        <EmptyState
          message="Você não tem permissão para ver o cadastro de pessoas."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  const resetTo = (change: () => void) => {
    change();
    setPage(1);
  };

  const items = list.data?.items ?? [];
  const total = list.data?.totalCount ?? 0;

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Cadastro"
        title="Partes"
        desc={DESCRIPTION}
        actions={
          <>
            <ExportMenu
              spec={{
                fileName: 'partes',
                columns: PARTY_EXPORT_COLUMNS,
                fetchPage: partyExportPage(filters),
              }}
              totalCount={total}
              disabled={list.isPending}
            />
            {canManage && (
              <Btn variant="primary" icon={Icons.plus} onClick={() => setShowNew(true)}>
                Nova pessoa
              </Btn>
            )}
          </>
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <FilterTabs
          tabs={[
            { value: 'todos', label: 'Todos' },
            { value: 'ativos', label: 'Ativos' },
            { value: 'inativos', label: 'Arquivados' },
          ]}
          value={tab}
          onChange={(v) => resetTo(() => setTab(v as StatusFilter))}
        />
        <SearchBox
          value={search}
          onChange={(v) => resetTo(() => setSearch(v))}
          placeholder="Buscar por nome, CPF ou e-mail…"
          width={320}
        />
      </div>

      {setActive.isError && (
        <div role="alert" style={noticeStyle}>
          <StrokeIcon d={Icons.alert} size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Não foi possível alterar o estado desta pessoa. Tente de novo.</span>
        </div>
      )}

      {list.isPending ? (
        <Skeleton rows={6} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          message={debouncedSearch ? 'Nenhuma pessoa encontrada para esta busca.' : 'Nenhuma pessoa cadastrada ainda.'}
          hint={
            debouncedSearch
              ? 'Ajuste a busca ou o filtro.'
              : 'As pessoas aparecem aqui quando são cadastradas nesta tela ou pelo cadastro de Empresa.'
          }
        />
      ) : (
        <>
          <Table
            columns={[
              { label: 'Pessoa' },
              { label: 'CPF' },
              { label: 'E-mail' },
              { label: 'Telefone' },
              { label: 'Empresas' },
              { label: 'Status' },
              { label: '', align: 'right', width: 150 },
            ]}
          >
            {items.map((party) => (
              <tr key={party.id} className="pa-row">
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, opacity: party.active ? 1 : 0.55 }}>
                    <Avatar name={party.name} size={34} />
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-900)' }}>{party.name}</span>
                  </div>
                </Td>
                <Td>
                  {party.cpf ? (
                    <CopyableText value={maskCPF(party.cpf)} style={{ fontSize: 13, fontFamily: 'var(--font-mono)' }} />
                  ) : (
                    <Missing />
                  )}
                </Td>
                <Td>
                  {party.email ? (
                    <CopyableText value={party.email} style={{ fontSize: 13, color: 'var(--fg-muted)' }} />
                  ) : (
                    <Missing />
                  )}
                </Td>
                <Td>
                  {party.phone ? (
                    <CopyableText value={party.phone} style={{ fontSize: 13, color: 'var(--fg-muted)' }} />
                  ) : (
                    <Missing />
                  )}
                </Td>
                <Td>
                  {/* Zero is the case worth showing: a person nobody linked is invisible to every scoped
                      operator, which looks exactly like a person who was never registered. */}
                  <button
                    type="button"
                    onClick={() => setAssociations(party)}
                    title={party.companyCount ? 'Ver e gerir vínculos' : 'Vincular a uma empresa'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '3px 9px',
                      border: `1px solid ${party.companyCount ? 'var(--border-default)' : 'var(--border-strong)'}`,
                      borderRadius: 999,
                      background: party.companyCount ? 'var(--cream-50)' : 'var(--cream-100)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 12,
                      color: party.companyCount ? 'var(--forest-800)' : 'var(--ink-900)',
                    }}
                  >
                    <StrokeIcon d={party.companyCount ? Icons.building : Icons.alert} size={12} />
                    {party.companyCount
                      ? `${party.companyCount} ${party.companyCount === 1 ? 'empresa' : 'empresas'}`
                      : 'Sem vínculo'}
                  </button>
                </Td>
                <Td>
                  <StatusBadge status={party.active ? 'ativo' : 'inativo'} />
                </Td>
                <Td align="right">
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {canManage && (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditing(party)}
                          className="pa-icon-btn"
                          style={{ width: 30, height: 30 }}
                          title="Editar"
                        >
                          <StrokeIcon d={Icons.pen} size={14} />
                        </button>
                        {party.active ? (
                          <Btn variant="secondary" size="sm" onClick={() => setToArchive(party)}>
                            Arquivar
                          </Btn>
                        ) : (
                          <Btn
                            variant="secondary"
                            size="sm"
                            icon={Icons.check}
                            onClick={() => setActive.mutate({ id: party.id, active: true })}
                          >
                            Reativar
                          </Btn>
                        )}
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
          <Pagination
            page={page}
            pageCount={Math.max(1, Math.ceil(total / PARTY_PAGE_SIZE))}
            total={total}
            onChange={setPage}
          />
        </>
      )}

      {showNew && <PartyFormModal onClose={() => setShowNew(false)} />}
      {editing && <PartyFormModal partyId={editing.id} onClose={() => setEditing(null)} />}
      {associations && (
        <PartyAssociationsModal
          partyId={associations.id}
          partyName={associations.name}
          canManage={canManage}
          onClose={() => setAssociations(null)}
          onOpenCompany={(companyId) => {
            setAssociations(null);
            navigate(`/empresas/${companyId}`);
          }}
        />
      )}
      {toArchive && (
        <ConfirmDialog
          title="Arquivar pessoa"
          message={
            <>
              Arquivar <strong>{toArchive.name}</strong>? Ela deixa de ser sugerida no cadastro de Empresa,
              mas o registro e os vínculos são preservados — contratos e histórico apontam para ela — e dá
              para reativar depois.
            </>
          }
          confirmLabel="Arquivar"
          icon={Icons.eyeOff}
          onConfirm={() => setActive.mutate({ id: toArchive.id, active: false })}
          onClose={() => setToArchive(null)}
        />
      )}
    </div>
  );
}

const DESCRIPTION =
  'Pessoas que respondem pelas empresas — representante legal e contato focal. Não fazem login no sistema.';

/** A field the directory simply does not have; the legacy keeps most of these people half-filled. */
function Missing() {
  return <span style={{ color: 'var(--stone-400)' }}>—</span>;
}

const noticeStyle = {
  padding: '10px 14px',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cream-100)',
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'var(--fg-muted)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
} as const;
